import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { suggestionsService } from '../services/api';

const Suggestions = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [replyStatus, setReplyStatus] = useState('a_reflechir');
  const [replyText, setReplyText] = useState('');
  const [savingReply, setSavingReply] = useState(false);

  const getStatusLabel = (status) => {
    if (status === 'valider') return 'Valider';
    if (status === 'refuse') return 'Refuse';
    return 'A Reflechir';
  };

  const getStatusColor = (status) => {
    if (status === 'valider') return 'success';
    if (status === 'refuse') return 'error';
    return 'warning';
  };

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const response = await suggestionsService.list({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
      });
      const data = response.data || {};
      setItems(data.suggestions || []);
      setTotal(Number(data.pagination?.total || 0));
    } catch (error) {
      console.error('Erreur chargement suggestions:', error);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [page, rowsPerPage, searchTerm]);

  const openReplyDialog = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setReplyStatus(
      ['valider', 'a_reflechir', 'refuse'].includes(suggestion.status)
        ? suggestion.status
        : 'a_reflechir'
    );
    setReplyText(suggestion.adminResponse || '');
  };

  const closeReplyDialog = () => {
    setSelectedSuggestion(null);
    setReplyStatus('a_reflechir');
    setReplyText('');
  };

  const saveReply = async () => {
    if (!selectedSuggestion) return;
    try {
      setSavingReply(true);
      await suggestionsService.update(selectedSuggestion.id, {
        status: replyStatus,
        adminResponse: replyText.trim() || null,
      });
      closeReplyDialog();
      await fetchSuggestions();
    } catch (error) {
      console.error('Erreur sauvegarde reponse suggestion:', error);
      alert('Impossible de sauvegarder la reponse');
    } finally {
      setSavingReply(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Suggestions utilisateurs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Liste des suggestions envoyées depuis l'application mobile.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Rechercher une suggestion, un email, un nom..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(0);
                setSearchTerm(searchInput.trim());
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>Suggestion</TableCell>
                  <TableCell>Contexte</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Plateforme</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ py: 2 }}>
                        <CircularProgress size={22} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        Aucune suggestion
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.createdAt ? dayjs(s.createdAt).format('DD/MM/YYYY HH:mm') : '—'}</TableCell>
                      <TableCell>
                        {s.user?.firstName || s.user?.first_name || '—'} {s.user?.lastName || s.user?.last_name || ''}
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {s.user?.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {s.message}
                        </Typography>
                      </TableCell>
                      <TableCell>{s.context || '—'}</TableCell>
                      <TableCell>{s.source || '—'}</TableCell>
                      <TableCell>{s.platform || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(s.status)}
                          size="small"
                          color={getStatusColor(s.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" onClick={() => openReplyDialog(s)}>
                          Repondre
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>

      <Dialog open={!!selectedSuggestion} onClose={closeReplyDialog} maxWidth="md" fullWidth>
        <DialogTitle>Repondre a la suggestion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Suggestion: {selectedSuggestion?.message}
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="suggestion-status-label">Etat</InputLabel>
            <Select
              labelId="suggestion-status-label"
              label="Etat"
              value={replyStatus}
              onChange={(e) => setReplyStatus(e.target.value)}
            >
              <MenuItem value="valider">Valider</MenuItem>
              <MenuItem value="a_reflechir">A Reflechir</MenuItem>
              <MenuItem value="refuse">Refuse</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Reponse au client"
            multiline
            minRows={4}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Votre retour sur cette suggestion..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReplyDialog}>Annuler</Button>
          <Button onClick={saveReply} variant="contained" disabled={savingReply}>
            {savingReply ? 'Sauvegarde...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Suggestions;

